import React, { HTMLAttributes, useCallback, useState } from "react"

import { cn } from "@/lib/utils"
import { IconCopy } from "@tabler/icons-react"

import { Button, ButtonProps } from "./Button"
import { Tooltip } from "./Tooltip"

export interface CopyButtonProps {
  className?: string
  text: string
  tooltip?: string
  icon?: ButtonProps<"button">["icon"]
  size?: ButtonProps<"button">["size"]
  position?: "left" | "right"
}

export interface CopyToClipboardProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  text: string
  children?: React.ReactNode
  size?: ButtonProps<"button">["size"]
  buttonPosition?: "left" | "right"
}

export const CopyToClipboard = ({
  text,
  children,
  size = "small",
  buttonPosition = "right",
  ...props
}: CopyToClipboardProps) => {
  return (
    <span {...props} className="inline-flex gap-1 align-middle">
      {buttonPosition === "left" && (
        <CopyButton text={text} size={size} position={buttonPosition} />
      )}
      {children || text}
      {buttonPosition === "right" && (
        <CopyButton text={text} size={size} position={buttonPosition} />
      )}
    </span>
  )
}

export const CopyButton = ({
  className,
  text,
  tooltip,
  icon = IconCopy,
  size = "small",
}: CopyButtonProps) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault()
      navigator.clipboard.writeText(text)
      setCopied(true)
    },
    [text],
  )

  return (
    <Tooltip
      onOpenChange={(open) => {
        if (!open) setCopied(false)
      }}
      content={copied ? "Copied!" : tooltip || "Copy to clipboard"}
      dark
    >
      <Button
        className={cn("-my-1 inline align-middle", className)}
        variant="neutral"
        size={size}
        noBorder
        iconOnly
        icon={icon}
        onClick={handleCopy}
      />
    </Tooltip>
  )
}
